import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public visitor-pass landing page (Module 7.4).
 * Standalone — no admin chrome. First name only, org logo, QR.
 * Cache-Control: no-store is set by the API; the page does not store the token in localStorage.
 */
export default function VisitorPass() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/public/visitor-pass/${token}`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "Pass not found or revoked");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-navy-900 text-white flex flex-col items-center justify-center p-8" data-testid="visitor-pass-error">
        <AlertCircle className="w-12 h-12 text-orange-400 mb-4" />
        <h1 className="text-2xl font-semibold mb-2">Pass unavailable</h1>
        <p className="text-sm text-navy-200">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-navy-900 text-white flex items-center justify-center" data-testid="visitor-pass-loading">
        <div className="text-sm text-navy-200">Loading your pass…</div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-navy-900 text-white flex flex-col items-center justify-center p-6" data-testid="visitor-pass-page">
      <div className="w-full max-w-sm bg-white rounded-3xl p-7 text-navy-900 shadow-2xl">
        {data.org_logo_url ? (
          <img src={data.org_logo_url} alt={data.org_name} className="h-12 mx-auto mb-5 object-contain" />
        ) : (
          <div className="h-12 flex items-center justify-center mb-5">
            <div className="w-10 h-10 rounded-xl bg-navy-900 text-white flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
          </div>
        )}
        <div className="eb-overline text-center mb-1" data-testid="visitor-pass-org">{data.org_name}</div>
        <h1 className="text-center text-3xl font-semibold mb-1" data-testid="visitor-pass-firstname">
          Welcome, {data.first_name}
        </h1>
        <p className="text-center text-sm text-navy-500 mb-5">Show this at the kiosk to sign in faster.</p>
        <div className="flex justify-center" data-testid="visitor-pass-qr-wrap">
          {data.qr_data_url && (
            <img src={data.qr_data_url} alt="QR pass" className="w-48 h-48 rounded-lg border border-navy-100" data-testid="visitor-pass-qr" />
          )}
        </div>
        <p className="text-center text-[11px] text-navy-400 mt-5">
          This pass is unique to you and refreshes on each visit. If lost, ask reception to reset.
        </p>
      </div>
      <div className="mt-6 text-[10px] uppercase tracking-[0.2em] text-navy-300">Powered by EntryBook</div>
    </div>
  );
}
